'use client';

import { Card, CardBody, Button, Chip, Divider, useDisclosure } from '@heroui/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { useState, useEffect, useMemo, useRef } from 'react';

import dynamic from 'next/dynamic';
import VehicleDetailSkeleton from './VehicleDetailSkeleton';
import FotoOptimizada from './FotoOptimizada';
// Los dos modales se abren recién con un clic: sus bundles (galería a
// pantalla completa y agenda, ~65 KB) se bajan en ese momento y no con la
// ficha. Sin SSR porque son portales que sólo existen en el navegador.
const VehicleImagesModal = dynamic(() => import('./VehicleImagesModal'), { ssr: false });
const BookingModal = dynamic(() => import('../booking/BookingModal'), { ssr: false });
import type { Client, Vehicle } from '../../utils/types';
import { mapTransmissionTypeToSpanish, contactByWhatsApp, dealershipLabel, etiquetaCatalogo, showVehicleBooking, showVehicleLocation, telefonoConMas } from '@/utils/functions';
import { isBrasil, forcedSiteLanguage } from '@/lib/country';
import useCustomerStore from '@/store/useCustomerStore';
import useThemeStore from '@/store/useThemeStore';
import { supabase } from '@/lib/supabase';
import { formatCurrency, currencyConfig } from '@/lib/currency';
import { modeloConVersion } from '@/lib/modeloConVersion';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { tDelTenant } from '@/i18n/delTenant';
import { precioModoDelCliente, type PrecioWebModo } from '@/lib/precio-web';
import { PriceBlock } from './PrecioFicha';
import { businessTimezone, todayYMDIn, slotIsFuture, type SlotUtc } from '../booking/bookingShared';
import { loadAvailability } from '../booking/loadAvailability';

/** Toma un color de marca/acento disponible */
function pickAccent(v: any): string {
  return (
    v?.theme_color ||
    v?.accent_color ||
    v?.brand?.primary_color ||
    v?.brand_color ||
    v?.ui_color ||
    '#6C2CF9'
  );
}

/**
 * Chile: la regla de siempre de ESTA ficha para el teléfono del vendedor /
 * sucursal (sin el caso "56 + 8 dígitos" que sí entiende `contact-utils`). Se
 * conserva tal cual; los demás países van por el récord de `lib/country`.
 */
const fmtChile = (n: string): string | null => {
  if (n.length === 11 && n.startsWith('569')) return n;
  if (n.length === 9 && n.startsWith('9')) return `56${n}`;
  if (n.length === 8) return `569${n}`;
  if (n.length === 12 && n.startsWith('0569')) return n.slice(1);
  if (n.length === 13 && n.startsWith('00569')) return n.slice(2);
  return null;
};

// Comentario para merge equisde
/** Tarjeta de especificación (no se muestra si value está vacío) */
interface DetailCardProps {
  icon: string;
  label: string;
  value: string;
  className?: string;
}
function DetailCard({ icon, label, value, className }: DetailCardProps) {
  const val = String(value ?? '').trim();
  if (!val) return null;
  return (
    <div className={`group relative w-full ${className || ''}`} role="listitem" aria-label={`${label}: ${val}`}>
      <Card className="relative rounded-2xl bg-gray-50/80 dark:bg-white/5 border border-gray-100 dark:border-gray-800 hover:border-primary/40 transition">
        <CardBody className="flex flex-col items-center justify-center gap-1.5 p-4">
          <Icon icon={icon} className="text-lg text-gray-700 dark:text-gray-200" />
          {/* label SIN scroll, con ellipsis si no cabe */}
          <p
            className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
            title={label}
          >
            {label}
          </p>
          {/* valor SIN scroll, con ellipsis si no cabe */}
          <p
            className="text-[13px] font-semibold text-gray-900 dark:text-white text-center w-full overflow-hidden text-ellipsis whitespace-nowrap"
            title={val}
          >
            {val}
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

/** Fila de miniaturas con tamaño contenido cuando hay < 4 imágenes */
function ThumbRow({
  images,
  onOpenModal,
  altBase = 'Vehículo',
  verGaleriaLabel = 'Ver toda la galería',
  gap = 12,
  aspect = 10 / 7,
  minThumb = 92,
  maxThumb = 148,
  maxCols = 6,
}: {
  images: string[];
  onOpenModal: (img?: string) => void;
  altBase?: string;
  /** Rótulo accesible del botón "+N" (ya traducido por quien llama). */
  verGaleriaLabel?: string;
  gap?: number;
  aspect?: number;
  minThumb?: number;
  maxThumb?: number;
  maxCols?: number;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState({ cols: 0, thumbW: 0, thumbH: 0, show: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const compute = () => {
      const W = el.clientWidth || 0;
      if (!W) return;

      const count = images.length;

      // Si hay menos de 4 imágenes, no llenar el ancho: usar un ancho fijo (clamp) por thumb.
      if (count > 0 && count < 4) {
        const thumbW = Math.min(maxThumb, Math.max(minThumb, Math.floor(W / 4 - gap)));
        const thumbH = Math.round(thumbW / aspect);
        setLayout({ cols: count, thumbW, thumbH, show: count });
        return;
      }

      // Fluido normal (intenta casar columnas dentro de un rango cómodo)
      let bestCols = 1;
      let bestW = W;

      for (let cols = 1; cols <= maxCols; cols++) {
        const totalGap = gap * (cols - 1);
        const w = (W - totalGap) / cols;
        if (w >= minThumb && w <= maxThumb) {
          bestCols = cols;
          bestW = w;
        }
      }

      if (bestCols === 1) {
        for (let cols = 1; cols <= maxCols; cols++) {
          const totalGap = gap * (cols - 1);
          const w = (W - totalGap) / cols;
          if (w >= minThumb) { bestCols = cols; bestW = w; break; }
          if (cols === maxCols) { bestCols = cols; bestW = w; }
        }
      }

      const needsPlus = count > bestCols;
      const visible = needsPlus ? bestCols - 1 : Math.min(count, bestCols);
      const totalGapVisible = gap * (visible + (needsPlus ? 1 : 0) - 1);
      const tiles = visible + (needsPlus ? 1 : 0);
      const thumbW = (W - totalGapVisible) / tiles;
      const thumbH = Math.round(thumbW / aspect);

      setLayout({ cols: bestCols, thumbW, thumbH, show: visible });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [gap, aspect, minThumb, maxThumb, maxCols, images.length]);

  if (!images?.length) return null;

  const canShowAll = images.length <= layout.cols || images.length < 4; // si <4, no hay "+n"
  const thumbs = canShowAll
    ? images.slice(0, layout.show || images.length)
    : images.slice(0, Math.max(0, layout.show));
  const remaining = Math.max(0, images.length - thumbs.length);

  const rowStyle: React.CSSProperties =
    images.length < 4
      ? { gap, justifyContent: 'flex-start' } // no estirar cuando hay pocas
      : { gap };

  return (
    <div ref={wrapRef} className="w-full min-w-0">
      {/* FIX desplazamiento en md: se elimina md:px-2 */}
      <div className="flex items-center " style={rowStyle}>
        {thumbs.map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={() => onOpenModal(src)}
            className="relative overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-primary transition"
            style={{ width: layout.thumbW, height: layout.thumbH, flex: '0 0 auto' }}
            aria-label={`${altBase} (${i + 1}/${images.length})`}
          >
            {/* La miniatura se ve a ~120 px pero bajaba el archivo completo
                (1200×900): ~790 KB por ficha para mostrar seis cuadraditos. */}
            <FotoOptimizada
              src={src}
              alt={`${altBase} (${i + 1}/${images.length})`}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: 'center center' }}
              sizes="160px"
              draggable={false}
            />
          </button>
        ))}

        {!canShowAll && (
          <button
            type="button"
            onClick={() => onOpenModal(thumbs[0] || images[0])}
            className="grid place-content-center rounded-xl bg-gray-100 dark:bg-dark-card ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-primary transition"
            style={{ width: layout.thumbW, height: layout.thumbH, flex: '0 0 auto' }}
            aria-label={verGaleriaLabel}
          >
            <Icon icon="mdi:image-multiple" className="text-2xl text-gray-700 dark:text-gray-300" />
            <span className="text-xs text-gray-700 dark:text-gray-300">+{remaining}</span>
          </button>
        )}
      </div>
    </div>
  );
}

interface VehicleDetailSectionProps {
  vehicle: Vehicle | null;
  loading?: boolean;
  client?: Client;
  /** URL canónica de la ficha (server-side); evita window.location en el primer render */
  pageUrl?: string;
  /**
   * true cuando client trae la config real del sitio (store hidratado).
   * El initialClient del header x-client-data NO trae client_website_config y
   * showVehicleLocation() con el campo ausente devuelve true — sin este gate,
   * el SSR pintaría la sucursal en tenants que la apagaron (flash + dato
   * oculto en el HTML crawleable). Default true para no cambiar otros callers.
   */
  locationKnown?: boolean;
  /**
   * Ticket #171: cómo se muestran los dos precios (ver `lib/precio-web`). Lo
   * manda `page.tsx` leído de la fila del tenant, porque el `client` del SSR
   * (header x-client-data) no trae la columna y el HTML pintaría el modo por
   * defecto hasta hidratar. Sin el prop se lee del `client`.
   */
  precioModo?: PrecioWebModo;
  onLike?: (vehicleId: string) => Promise<void>;
  isLiked?: boolean;
}

export default function VehicleDetailSection({
  vehicle,
  loading = false,
  client,
  pageUrl,
  locationKnown = true,
  precioModo: precioModoProp,
  onLike,
  isLiked = false,
}: VehicleDetailSectionProps) {
  const precioModo = precioModoProp ?? precioModoDelCliente(client);
  const { t } = useTranslation();
  // Textos fijos de la ficha (catalog.*), por el i18n del sitio: un sitio de
  // EE.UU. los ve en inglés (instancia `i18nEn`). Si la clave no resuelve, el
  // español de siempre.
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    return !val || val === key ? fallback : val;
  };
  // País del tenant: SOLO para lo que es propio de Chile (teléfono +56, valores
  // de catálogo en español). Brasil = 'BR' (pt) y EE.UU. = 'US' (en); cualquier
  // otro, lo de siempre.
  const country = client?.country;
  const esBrasil = isBrasil(country);
  const idiomaFijo = forcedSiteLanguage(country);
  // El mensaje prellenado de WhatsApp lo recibe la AUTOMOTORA: va en el idioma
  // del tenant (CL: el español de siempre, mire el visitante en el idioma que
  // mire; BR: pt; US: en).
  const mensajeWhatsApp = () => {
    const val = tDelTenant(country)('catalog.detail.whatsappMessage');
    return !val || val === 'catalog.detail.whatsappMessage' ? 'Hola, me interesa el {{vehicle}} que vi en {{url}}' : val;
  };
  // Formateo puro por moneda del tenant (server-safe): useCurrency lee el store
  // zustand, que en SSR es null → formatearía BRL como CLP y además cambiaría
  // entre server y client (hydration mismatch).
  const formatPrice = (n: number) => formatCurrency(n, client?.currency, { country: client?.country });
  const tenantLocale = currencyConfig(client?.currency).locale;
  const { theme } = useThemeStore();

  const isLoadingUI = loading || !vehicle;
  const v = (vehicle ?? {}) as Vehicle;

  const images = useMemo(
    () => (v?.main_image ? [v.main_image, ...(v.gallery || [])].filter(Boolean) : []),
    [v?.main_image, v?.gallery]
  );

  const ACCENT = pickAccent(v);

  // Obtener el color primary del cliente (mismo que usa bg-primary del botón)
  const primaryColor = useMemo(() => {
    if (client?.theme) {
      const themeColors = theme === 'dark' ? client.theme.dark : client.theme.light;
      return themeColors?.primary || ACCENT;
    }
    return ACCENT;
  }, [client?.theme, theme, ACCENT]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentModalImage, setCurrentModalImage] = useState<string>('');
  const handleImageClick = (img: string) => {
    setCurrentModalImage(img);
    onOpen();
  };

  // ===== Store cliente =====
  const { setIsModalOpen } = useCustomerStore();

  const [sellerPhone, setSellerPhone] = useState<string | null>(null);
  const [dealershipPhone, setDealershipPhone] = useState<string | null>(null);
  // Teléfono con '+' por PAÍS del tenant (récord de lib/country): Chile con la
  // regla de siempre de esta ficha (`fmtChile`), Brasil con la suya, CO/AR nuevas.
  const fmtPhone = (p?: string | null) => (p ? telefonoConMas(p, country, { CL: fmtChile }) : null);
  useEffect(() => {
    if (isLoadingUI) return;
    const run = async () => {
      // Teléfono del vendedor por RPC y por AUTO (anon ya no lee `users`, pentest
      // 30-oct): solo responde si el auto se ve en la web. Si falla, `data` viene
      // null y getPhone cae al teléfono de la sucursal, como siempre.
      if (v?.seller_id && v?.id) {
        const { data } = await supabase.rpc('fn_vehicle_seller_public_phone', {
          p_vehicle_id: Number(v.id),
        });
        setSellerPhone(fmtPhone(typeof data === 'string' ? data : null));
      }
      if (v?.dealership_id) {
        const { data } = await supabase.from('dealerships').select('phone').eq('id', v.dealership_id).single();
        setDealershipPhone(fmtPhone(data?.phone));
      }
    };
    run();
  }, [isLoadingUI, v?.id, v?.seller_id, v?.dealership_id]);

  const getPhone = () =>
    sellerPhone || dealershipPhone || fmtPhone(client?.contact?.phone) || '';

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = `${v.brand?.name} ${v.model?.name} ${v.year}`;
    if (navigator.share && /mobile|android|iphone/i.test(navigator.userAgent)) {
      try { await navigator.share({ title, text: tx('catalog.detail.shareText', 'Mira este {{title}}').replace('{{title}}', () => title), url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success(tx('catalog.detail.linkCopied', '¡Enlace copiado!'), { autoClose: 1600 }); }
      catch { toast.error(tx('catalog.detail.copyError', 'Error al copiar'), { autoClose: 1600 }); }
    }
  };

  const handleLike = async () => {
    if (!client) return setIsModalOpen(true);
    if (onLike && v.id) await onLike(v.id);
  };

  const isSold = v.status?.name === 'Vendido';
  const isReserved = v.status?.name === 'Reservado';

  const price = v.price ?? 0;
  const formattedPrice = formatPrice(price);
  const discountedNumeric = v.discount_percentage ? price * (1 - (v.discount_percentage || 0) / 100) : null;
  const formattedDiscounted = discountedNumeric !== null ? formatPrice(discountedNumeric) : null;

  // ---------- ESTADO y LÓGICA DEL MODAL DE AGENDA ----------
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  // Verificar si hay horarios disponibles para mostrar el botón de agendar
  const [hasAvailableSlots, setHasAvailableSlots] = useState<boolean>(false);
  const [loadingAvailability, setLoadingAvailability] = useState<boolean>(true);

  useEffect(() => {
    if (isLoadingUI || !client?.id || !v?.dealership_id) {
      setHasAvailableSlots(false);
      setLoadingAvailability(false);
      return;
    }

    const checkAvailability = async () => {
      setLoadingAvailability(true);
      try {
        // Zona y reloj del NEGOCIO, no del visitante. Con el reloj local, este
        // chequeo escondía el botón "Agendar" de una automotora chilena a
        // cualquiera que la mirara desde un huso adelantado. Ver
        // `businessTimezone` en components/booking/bookingShared.
        const tz = businessTimezone(client);
        const todayYMD = todayYMDIn(tz);
        // Mediodía a propósito: es una fecha de calendario, no un instante, y
        // así el cambio de horario de verano no corre el día.
        const base = new Date(`${todayYMD}T12:00:00`);

        // Verificar los próximos 30 días para ver si hay al menos un slot disponible.
        //
        // La pregunta es una sola —"¿hay algún cupo futuro en los próximos 30
        // días?"— y la respuesta no depende del orden en que se consulten los
        // días. Antes se preguntaba día por día, esperando cada respuesta
        // (hasta 30 viajes en fila, ~8 s para una automotora sin agenda);
        // ahora se consultan en tandas de 5 en paralelo, la misma concurrencia
        // que usa el calendario (useMonthAvailability), y se corta en la
        // primera tanda que trae un cupo válido.
        const dias: string[] = [];
        for (let i = 0; i < 30; i++) {
          const checkDate = new Date(base);
          checkDate.setDate(base.getDate() + i);
          dias.push(`${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`);
        }

        // `loadAvailability` pide INSTANTES y cae al reloj de pared si la
        // base todavía no tiene las RPC `_utc`. `slotIsFuture` resuelve las
        // dos formas, así que acá desaparece el regex sobre el string.
        const diaTieneCupo = async (ymd: string): Promise<boolean> => {
          let slots: SlotUtc[] = [];
          try {
            ({ slots } = await loadAvailability(
              Number(client.id), Number(v.dealership_id), ymd, tz,
            ));
          } catch {
            slots = [];
          }
          return slots.length > 0 && slots.some((slot) => slotIsFuture(slot, ymd, tz));
        };

        const TANDA = 5;
        for (let i = 0; i < dias.length; i += TANDA) {
          const resultados = await Promise.all(dias.slice(i, i + TANDA).map(diaTieneCupo));
          if (resultados.some(Boolean)) {
            setHasAvailableSlots(true);
            setLoadingAvailability(false);
            return;
          }
        }
        setHasAvailableSlots(false);
      } catch (err) {
        console.error('Error checking availability:', err);
        setHasAvailableSlots(false);
      }
      setLoadingAvailability(false);
    };

    checkAvailability();
  }, [isLoadingUI, client?.id, v?.dealership_id]);

  if (isLoadingUI) {
    return <VehicleDetailSkeleton />;
  }

  // ===== Preparar specs (se ocultan si value vacío) =====
  const specs = [
    {
      icon: 'mdi:speedometer',
      label: t('vehicles.card.mileage'),
      value: (v.mileage ?? null) !== null ? `${Number(v.mileage).toLocaleString(tenantLocale)} ${t('common.units.km')}` : '',
    },
    {
      icon: 'mdi:gas-station',
      label: t('vehicles.card.fuel'),
      // Brasil: el valor de la base ("diesel", "eléctrico") en portugués. El
      // resto de los países, la capitalización de siempre.
      value: v.fuel_type?.name
        ? etiquetaCatalogo('fuel', v.fuel_type.name, country, v.fuel_type.name[0].toUpperCase() + v.fuel_type.name.slice(1))
        : '',
    },
    {
      icon: 'mdi:car-shift-pattern',
      label: t('vehicles.card.transmission'),
      value: idiomaFijo
        ? etiquetaCatalogo('transmission', v.transmission, country, mapTransmissionTypeToSpanish(v.transmission) || '')
        : mapTransmissionTypeToSpanish(v.transmission) || '',
    },
    {
      icon: 'mdi:palette',
      label: t('vehicles.card.color'),
      value: v.color?.name
        ? etiquetaCatalogo('color', v.color.name, country, v.color.name[0].toUpperCase() + v.color.name.slice(1))
        : '',
    },
  ].filter(s => String(s.value ?? '').trim() !== '');

  // ===== Sucursal del vehículo =====
  // v.dealership viene embebido desde getVehicleById; el fallback cubre fetches
  // viejos que solo traen dealership_id (el ClientProvider ya carga client.dealerships).
  const sucursal =
    v.dealership ??
    (v.dealership_id
      ? client?.dealerships?.find((d) => String(d.id) === String(v.dealership_id))
      : undefined) ??
    null;
  // Flag de sitio (Configuración → Sucursales): si está apagado, no se muestra
  // la ubicación del auto en ninguna parte del sitio.
  const showLocation = locationKnown && showVehicleLocation(client);
  /* ¿Esta automotora ofrece "Agendar visita" en la ficha? Apagado le saca una
     de las tres puertas del agendamiento web; el link /agendar y el ítem del
     menú siguen. No lleva el gate de `locationKnown` porque el botón ya espera
     la consulta de cupos del navegador y nunca se pinta en el SSR. */
  const ofreceAgendar = showVehicleBooking(client);
  const sucursalName = showLocation ? dealershipLabel(sucursal, country) : '';
  const sucursalMapsUrl = (() => {
    if (!sucursal) return null;
    const loc = (sucursal.location || {}) as { lat?: number; lng?: number };
    if (Number.isFinite(loc.lat) && Number.isFinite(loc.lng)) {
      return `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`;
    }
    const dir = (sucursal.address || '').trim();
    return dir ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dir)}` : null;
  })();

  // Base para alt text de todas las fotos: solo datos del vehículo (sin palabras
  // traducibles) para no depender de i18n. Guard: brand/model pueden venir null.
  const altBase =
    [v.brand?.name, v.model?.name, v.year].filter(Boolean).join(' ') || tx('catalog.common.vehicle', 'Vehículo');

  return (
    <div
      className="
        isolate
        max-w-[1280px] mx-auto w-full
        grid gap-6 sm:gap-8
        grid-cols-1
        /* Derecha un poquito más ancha en lg/xl */
        lg:[grid-template-columns:minmax(0,1fr)_minmax(440px,500px)]
        xl:[grid-template-columns:minmax(0,1fr)_minmax(500px,560px)]
        px-0 md:px-6
      "
    >
      {/* ===== Columna izquierda ===== */}
      {/* El sticky vale desde `lg`, que es donde el grid pasa a DOS columnas.
          Abajo de eso las "columnas" son filas apiladas y las dos eran sticky
          al mismo `top-24`: al scrollear, la galería quedaba clavada y la card
          de info —posterior en el DOM— se le montaba encima. Medido a 390px:
          369px de solapamiento tras scrollear 600px, con la galería invisible.
          El skeleton ya usa `md:sticky`, así que esto además saca el salto
          entre el esqueleto y la ficha ya cargada. */}
      <div className="space-y-4 min-w-0 lg:sticky lg:top-24 self-start px-px pb-2">
        <Card className="w-full relative dark:bg-dark-card dark:border-dark-border rounded-3xl overflow-hidden border border-gray-100 shadow-[0_8px_28px_rgba(0,0,0,0.08)]">
          {(isSold || isReserved) && (
            <div className="absolute top-0 right-0 h-[200px] w-[200px] overflow-hidden z-20">
              <div className={`absolute top-[30px] right-[-50px] ${isSold ? 'bg-sold' : 'bg-yellow-500'} text-white font-bold py-2 w-[250px] text-center transform rotate-45`}>
                {(isSold ? t('vehicles.status.sold') : t('vehicles.status.reserved')).toUpperCase()}
              </div>
            </div>
          )}

          <CardBody className="p-0 w-full">
            <div className="relative w-full overflow-hidden rounded-[22px]">
              <div className="relative aspect-[4/3] md:aspect-[16/11]]">
                {!!images[0] && (
                  // Es la imagen que mide el LCP de la ficha: va con
                  // `priority` (se precarga y no queda en carga diferida) y por
                  // el optimizador, que la entrega en AVIF a la mitad de peso.
                  <FotoOptimizada
                    alt={`${altBase} (1/${images.length})`}
                    className={`absolute inset-0 w-full h-full object-cover cursor-pointer ${isSold || isReserved ? 'opacity-85' : ''}`}
                    src={images[0]}
                    sizes="(max-width: 768px) 100vw, 60vw"
                    priority
                    onClick={() => handleImageClick(images[0])}
                  />
                )}
              </div>
              <div className="pointer-events-none absolute inset-0 ring-1 ring-black/5 dark:ring-white/10 rounded-[22px]" />
            </div>
          </CardBody>
        </Card>

        {images.length > 1 && (
          <ThumbRow
            images={images}
            altBase={altBase}
            verGaleriaLabel={tx('catalog.detail.viewGallery', 'Ver toda la galería')}
            onOpenModal={(img) => handleImageClick(img || images[0])}
            gap={12}
            aspect={10 / 7}
            minThumb={92}
            maxThumb={148}
            maxCols={6}
          />
        )}
      </div>

      {/* ===== Columna derecha ===== */}
      {/* Igual que la izquierda: sticky sólo con dos columnas reales (`lg`). */}
      <div className="min-w-0">
        <Card
          className="
             rounded-3xl overflow-hidden
            bg-white dark:bg-dark-card
            shadow-[0_8px_28px_rgba(0,0,0,0.08)]
            border border-gray-100 dark:border-gray-800
            lg:sticky lg:top-24
          "
        >
          {/* Header */}
          <div className="p-5 md:p-6 border-b border-gray-200/70 dark:border-gray-700/60">
            <div className="flex items-start justify-between gap-3 min-w-0">
              <div className="min-w-0">
                <h1 className="text-[26px] md:text-[32px] leading-tight font-extrabold text-gray-900 dark:text-white break-words">
                  {v.brand?.name} {modeloConVersion(v.model?.name, v.version_name, v.brand?.name)}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center h-7 px-2.5 rounded-full text-xs md:text-sm ring-1 ring-gray-200 dark:ring-gray-700 text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-white/5">
                    <Icon icon="mdi:calendar" className="text-base" />
                    {v.year}
                  </span>

                  {/* Ticket #171: en modo contado/financiamiento no hay "rebaja" que anunciar. */}
                  {v.discount_percentage && precioModo === 'descuento' ? (
                    <span className="inline-flex items-center h-7 px-2 rounded-md text-xs font-semibold bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30">
                      <Icon icon="mdi:tag-outline" className="text-base" />
                      -{Math.round(v.discount_percentage)}%
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Acciones: SOLO ÍCONOS (sin fondo ni borde) */}
              <div className="hidden sm:flex shrink-0 items-center gap-2">
                <Button
                  isIconOnly
                  radius="full"
                  variant="light"
                  size="lg"
                  onPress={handleShare}
                  aria-label={t('common.actions.share')}
                  className="hover:opacity-80 text-primary"
                >
                  <Icon icon="mdi:share-variant" className="text-2xl" />
                </Button>

                <Button
                  isIconOnly
                  radius="full"
                  variant="light"
                  size="lg"
                  onPress={handleLike}
                  aria-label={isLiked ? (idiomaFijo ? t('catalog.detail.unsave') : t('common.actions.unsave')) : t('common.actions.save')}
                  aria-pressed={isLiked}
                  className="hover:opacity-80"
                  style={{ background: 'transparent', color: '#e11d48' }}
                >
                  <Icon icon={isLiked ? 'mdi:heart' : 'mdi:heart-outline'} className="text-2xl" />
                </Button>
              </div>

              {/* Mobile: botones grandes (mejor área táctil) */}
              <div className="flex sm:hidden shrink-0 items-center gap-2">
                <Button
                  isIconOnly
                  radius="full"
                  variant="light"
                  size="lg"
                  onPress={handleShare}
                  aria-label={t('common.actions.share')}
                  className="hover:opacity-80 h-12 w-12 text-primary"
                >
                  <Icon icon="mdi:share-variant" className="text-[28px]" />
                </Button>

                <Button
                  isIconOnly
                  radius="full"
                  variant="light"
                  size="lg"
                  onPress={handleLike}
                  aria-label={isLiked ? (idiomaFijo ? t('catalog.detail.unsave') : t('common.actions.unsave')) : t('common.actions.save')}
                  aria-pressed={isLiked}
                  className="hover:opacity-80 h-12 w-12"
                  style={{ background: 'transparent', color: '#e11d48' }}
                >
                  <Icon icon={isLiked ? 'mdi:heart' : 'mdi:heart-outline'} className="text-[28px]" />
                </Button>
              </div>
            </div>
          </div>

          {/* Body */}
          <CardBody className="p-4 md:p-6 space-y-6">
            {/* Precio */}
            <PriceBlock v={v} formattedPrice={formattedPrice} discountedPrice={formattedDiscounted} modo={precioModo} />

            {/* CTAs (sin guardar abajo) */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <Button
                size="lg"
                as="a"
                startContent={<Icon icon="mdi:whatsapp" className="text-xl" />}
                href={contactByWhatsApp(
                  getPhone(),
                  // Lo lee la automotora: en el idioma del TENANT, no del visitante.
                  mensajeWhatsApp()
                    .replace('{{vehicle}}', () => `${v.brand?.name} ${v?.model?.name} ${v.year}`)
                    .replace('{{url}}', () => `${pageUrl ?? (typeof window !== 'undefined' ? window.location.href : '')}`),
                  country
                )}
                target="_blank"
                onPress={() => {
                  const fbq = (window as Window & { fbq?: (...args: unknown[]) => void }).fbq;
                  fbq?.('track', 'Contact', {
                    content_ids: [String(v.id)],
                    content_type: 'vehicle',
                  });
                }}
                className="sm:flex-1 bg-[#24d465]"
                aria-label={t('vehicles.card.contact')}
                style={{ color: '#fff', borderColor: '#0e7c3d' }}
              >
                {t('vehicles.card.contact')}
              </Button>

              {/* `ofreceAgendar`: la automotora puede apagar esta puerta sin
                  perder el agendamiento. Va junto a la condición de cupos que
                  ya existía —el botón nunca se pintó en el servidor, espera a
                  `hasAvailableSlots`— así que al sumarse acá no cambia cuándo
                  aparece ni introduce un parpadeo. Ver `showVehicleBooking`. */}
              {!loadingAvailability && hasAvailableSlots && ofreceAgendar && (
                <Button
                  size="lg"
                  variant="solid"
                  color="default"
                  startContent={<Icon icon="mdi:calendar-check" className="text-xl" />}
                  className="sm:flex-1 hover:opacity-95 bg-primary text-primary-foreground transition-[filter,opacity] focus:opacity-100"
                  onPress={openBooking}
                  aria-label={idiomaFijo === 'en' ? 'Book a visit' : 'Agendar visita'}
                >
                  {idiomaFijo === 'en' ? 'Book a visit' : 'Agendar visita'}
                </Button>
              )}
            </div>

            <Divider className="dark:border-dark-border" />

            {/* Specs (se muestra solo si hay alguna con valor) */}
            {specs.length > 0 && (
              <div role="list" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {specs.map((s, idx) => (
                  <DetailCard key={s.label + idx} icon={s.icon} label={s.label} value={s.value} />
                ))}
              </div>
            )}

            {/* Sucursal donde está el vehículo */}
            {sucursalName && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl bg-gray-50/80 dark:bg-white/5 border border-gray-100 dark:border-gray-800 p-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Icon icon="mdi:map-marker" className="text-2xl shrink-0 mt-0.5 text-gray-700 dark:text-gray-200" />
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {tx('catalog.detail.availableAt', 'Disponible en sucursal')}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                      {sucursalName}
                    </p>
                    {sucursal?.address && sucursal.address.trim() !== sucursalName && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 break-words">
                        {sucursal.address}
                      </p>
                    )}
                  </div>
                </div>
                {sucursalMapsUrl && (
                  <Button
                    as="a"
                    href={sucursalMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="flat"
                    className="shrink-0 self-start sm:self-center"
                    startContent={<Icon icon="mdi:directions" className="text-base" />}
                  >
                    {tx('catalog.detail.directions', 'Cómo llegar')}
                  </Button>
                )}
              </div>
            )}

            {/* Features */}
            {!!v?.features?.length && (
              <>
                <Divider className="dark:border-dark-border" />
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                    {t('vehicles.details.features')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {v.features.map((feature, i) => (
                      <Chip
                        key={`${feature}-${i}`}
                        variant="flat"
                        className="bg-gray-100 dark:bg-dark-card border-none rounded-md text-gray-700 dark:text-gray-300"
                        startContent={<Icon icon="mdi:check-circle-outline" className="text-base opacity-70" />}
                      >
                        {feature}
                      </Chip>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Descripción */}
            {!!v.description && (
              <>
                <Divider className="dark:border-dark-border" />
                <div className="rounded-2xl bg-white/60 dark:bg-white/5 border border-gray-100 dark:border-gray-800 p-4">
                  <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                    {t('vehicles.details.description')}
                  </h2>
                  {/* Respetar \n y cortar palabras largas sin scroll */}
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                    {v.description}
                  </p>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Modal imágenes */}
      <VehicleImagesModal
        isOpen={isOpen}
        onClose={onClose}
        currentImage={currentModalImage || images[0] || ''}
        images={images}
        altBase={altBase}
        onImageChange={setCurrentModalImage}
      />

      {/* MODAL AGENDA */}
      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        client={client}
        vehicle={v as Vehicle}
        primaryColor={primaryColor}
      />
    </div>
  );
}

// El bloque de precio vive en `./PrecioFicha` (ticket #171): mismo markup de
// siempre en modo 'descuento', y con rótulos en 'contado_financiamiento'.
